
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Calendar, Filter } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { DetailedLessonCard } from './DetailedLessonCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

interface LecturerScheduleData {
  lesson_id: string;
  lesson_number: number;
  lesson_date: string | null;
  topic: string | null;
  location: string | null;
  time: string | null;
  group_name: string;
  lecturer_name: string;
  lecturer_id: string;
  lecturer_color: string;
}

interface GroupOption {
  id: string;
  name: string;
}

export const LecturerScheduleOverview = () => {
  const [scheduleData, setScheduleData] = useState<LecturerScheduleData[]>([]);
  const [filteredData, setFilteredData] = useState<LecturerScheduleData[]>([]);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  useEffect(() => {
    setSelectedMonth(getCurrentMonth());
    fetchLecturerSchedule();
    fetchGroups();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [scheduleData, selectedGroup, selectedMonth]);

  const fetchGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('groups')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setGroups(data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...scheduleData];

    // Filter by group
    if (selectedGroup !== 'all') {
      filtered = filtered.filter(lesson => {
        // Find the group by ID and match against the lesson's group name
        const group = groups.find(g => g.id === selectedGroup);
        return group && lesson.group_name === group.name;
      });
    }

    // Filter by month
    if (selectedMonth) {
      filtered = filtered.filter(lesson => {
        if (!lesson.lesson_date) return false;
        const lessonMonth = lesson.lesson_date.substring(0, 7); // YYYY-MM
        return lessonMonth === selectedMonth;
      });
    }

    setFilteredData(filtered);
  };

  const fetchLecturerSchedule = async () => {
    try {
      console.log('Starting lecturer schedule fetch...');
      
      // Strategy: Use separate queries for better reliability
      console.log('Fetching lessons without embedding...');
      const lessonsResult = await supabase
        .from('lessons')
        .select('id, lesson_number, lesson_date, topic, location, time, lecturer_id, group_id')
        .order('lesson_date', { ascending: false });
      
      if (lessonsResult.error) {
        console.error('Error fetching lessons:', lessonsResult.error);
        throw lessonsResult.error;
      }
      
      console.log('Raw lessons data:', lessonsResult.data);
      
      if (!lessonsResult.data || lessonsResult.data.length === 0) {
        console.log('No lessons found');
        setScheduleData([]);
        setLoading(false);
        return;
      }

      // Get unique group IDs
      const groupIds = [...new Set(lessonsResult.data.map(lesson => lesson.group_id))].filter(Boolean) as string[];
      console.log('Group IDs to fetch:', groupIds);
      
      // Fetch groups separately
      const groupsResult = await supabase
        .from('groups')
        .select('id, name')
        .in('id', groupIds);
      
      if (groupsResult.error) {
        console.error('Error fetching groups:', groupsResult.error);
        throw groupsResult.error;
      }
      
      console.log('Groups data:', groupsResult.data);
      
      // Get unique lecturer IDs
      const lecturerIds = [...new Set(lessonsResult.data.map(lesson => lesson.lecturer_id))].filter(Boolean) as string[];
      console.log('Lecturer IDs:', lecturerIds);

      // Skip if no lecturer IDs found
      if (lecturerIds.length === 0) {
        console.warn('No lecturer IDs found in lessons data');
        setScheduleData([]);
        setLoading(false);
        return;
      }

      // Fetch lecturer profiles with email as fallback
      const { data: lecturerProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', lecturerIds);

      if (profilesError) {
        console.error('Error fetching lecturer profiles:', profilesError);
        throw profilesError;
      }

      console.log('Lecturer profiles:', lecturerProfiles);

      // Get lecturer colors using the database function
      const lecturerColors: { [key: string]: string } = {};
      for (const lecturerId of lecturerIds) {
        try {
          const { data: colorData, error: colorError } = await supabase
            .rpc('get_lecturer_color', { lecturer_uuid: lecturerId });
          
          if (!colorError && colorData) {
            lecturerColors[lecturerId] = colorData;
          } else {
            console.warn(`Could not get color for lecturer ${lecturerId}:`, colorError);
            lecturerColors[lecturerId] = 'bg-muted';
          }
        } catch (colorError) {
          console.warn(`Error getting color for lecturer ${lecturerId}:`, colorError);
          lecturerColors[lecturerId] = 'bg-muted';
        }
      }

      console.log('Lecturer colors:', lecturerColors);

      // Manually join the data and format
      const formattedData: LecturerScheduleData[] = lessonsResult.data.map(lesson => {
        const lecturer = lecturerProfiles?.find(p => p.id === lesson.lecturer_id);
        const group = groupsResult.data?.find(g => g.id === lesson.group_id);
        
        // Improved lecturer name generation with proper fallbacks
        let lecturerName = 'Անհայտ դասախոս';
        if (lecturer) {
          const firstName = lecturer.first_name?.trim() || '';
          const lastName = lecturer.last_name?.trim() || '';
          
          if (firstName && lastName) {
            lecturerName = `${firstName} ${lastName}`;
          } else if (firstName) {
            lecturerName = firstName;
          } else if (lastName) {
            lecturerName = lastName;
          } else if (lecturer.email) {
            // Use email as fallback if no names available
            lecturerName = lecturer.email.split('@')[0];
          }
        }
        
        return {
          lesson_id: lesson.id,
          lesson_number: lesson.lesson_number,
          lesson_date: lesson.lesson_date,
          topic: lesson.topic,
          location: lesson.location,
          time: lesson.time,
          group_name: group ? group.name : 'Անհայտ խումբ',
          lecturer_name: lecturerName,
          lecturer_id: lesson.lecturer_id,
          lecturer_color: lecturerColors[lesson.lecturer_id] || 'bg-muted'
        };
      });

      console.log('Formatted schedule data:', formattedData);
      setScheduleData(formattedData);
    } catch (error) {
      console.error('Error fetching lecturer schedule:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել դասացուցակը. Խնդրում ենք նորից փորձել:",
        variant: "destructive"
      });
      // Set empty data instead of leaving in loading state
      setScheduleData([]);
    } finally {
      setLoading(false);
    }
  };

  const weekDays = [
    'Երկուշաբթի',
    'Երեքշաբթի', 
    'Չորեքշաբթի',
    'Հինգշաբթի',
    'Ուրբաթ',
    'Շաբաթ',
    'Կիրակի'
  ];

  const monthNames = [
    'Հունվար', 'Փետրվար', 'Մարտ', 'Ապրիլ', 'Մայիս', 'Հունիս',
    'Հուլիս', 'Օգոստոս', 'Սեպտեմբեր', 'Հոկտեմբեր', 'Նոյեմբեր', 'Դեկտեմբեր'
  ];

  const getMonthOptions = () => {
    const options = [];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear - 1; year <= currentYear + 1; year++) {
      for (let month = 1; month <= 12; month++) {
        const value = `${year}-${String(month).padStart(2, '0')}`;
        const label = `${monthNames[month - 1]} ${year}`;
        options.push({ value, label });
      }
    }
    
    return options;
  };

  const clearFilters = () => {
    setSelectedGroup('all');
    setSelectedMonth(getCurrentMonth());
  };

  const groupLessonsByWeekday = () => {
    const grouped: { [key: string]: LecturerScheduleData[] } = {};
    
    // Initialize all weekdays
    weekDays.forEach(day => {
      grouped[day] = [];
    });

    filteredData.forEach(lesson => {
      if (lesson.lesson_date) {
        const date = new Date(lesson.lesson_date);
        const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1; // Convert to Monday = 0, Sunday = 6
        const dayName = weekDays[adjustedIndex];
        grouped[dayName].push(lesson);
      }
    });

    // Sort lessons within each day by time
    Object.keys(grouped).forEach(dayName => {
      grouped[dayName].sort((a, b) => {
        if (!a.time && !b.time) return 0;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      });
    });

    return grouped;
  };

  if (loading) {
    return (
      <Card className="border-border/70 bg-card/80">
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Բեռնվում է...</div>
        </CardContent>
      </Card>
    );
  }

  const groupedLessons = groupLessonsByWeekday();

  return (
    <div className="space-y-6">
      <Card className="border-border/70 bg-card/80">
        <CardHeader>
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Շաբաթացույց - Դասախոսների դասացուցակ
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground text-sm">Ֆիլտներ</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Group Filter */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Խումբ</label>
                <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                  <SelectTrigger className="bg-muted border-border/70 text-muted-foreground">
                    <SelectValue placeholder="Ընտրել խումբը" />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border/70">
                    <SelectItem value="all" className="text-muted-foreground">Բոլոր խմբերը</SelectItem>
                    {groups.map((group) => (
                      <SelectItem key={group.id} value={group.id} className="text-muted-foreground">
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Month Filter */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Ամիս</label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="bg-muted border-border/70 text-muted-foreground">
                    <SelectValue placeholder="Ընտրել ամիսը" />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border/70">
                    {getMonthOptions().map((option) => (
                      <SelectItem key={option.value} value={option.value} className="text-muted-foreground">
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters Button */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground opacity-0">Գործողություն</label>
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="w-full border-border/70 text-muted-foreground hover:bg-primary/10"
                >
                  Մաքրել ֆիլտրերը
                </Button>
              </div>
            </div>

            {/* Results summary */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Ցուցադրվում է {filteredData.length} դաս</span>
              {(selectedGroup !== 'all' || selectedMonth !== getCurrentMonth()) && (
                <Badge variant="outline" className="text-muted-foreground border-border/70">
                  Ֆիլտրված
                </Badge>
              )}
            </div>
          </div>

          {filteredData.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">
                {scheduleData.length === 0 ? 'Դեռ դասեր չկան' : 'Ֆիլտրերի համապատասխան դասեր չեն գտնվել'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-4">
              {weekDays.map((dayName) => {
                const dayLessons = groupedLessons[dayName] || [];
                return (
                  <div key={dayName} className="flex flex-col min-w-0">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-2 md:p-3 bg-muted rounded-lg border border-border/70 mb-3">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Calendar className="h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" />
                        <h3 className="text-sm md:text-lg font-bold text-muted-foreground truncate">{dayName}</h3>
                      </div>
                      <Badge variant="outline" className="text-muted-foreground border-border/70 flex-shrink-0 text-xs">
                        {dayLessons.length}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 min-h-[150px] md:min-h-[200px] flex-1">
                      {dayLessons.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-muted-foreground text-xs md:text-sm">Դասեր չկան</p>
                        </div>
                      ) : (
                        dayLessons.map((lesson) => (
                          <DetailedLessonCard key={lesson.lesson_id} lesson={lesson} />
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
